import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { toast } from "react-hot-toast";
import { fetchUserProfile, updateUserProfile } from "../../actions/authAction/loginAction";
import UserAvatar from "../../components/UserAvatar";
import { resolveAssetUrl } from "../../utils/helpers/storeSetting";
import { FieldFeedback, FormValidationNotice, RequiredMark } from "../../components/FormValidation";
import useFormValidation from "../../hooks/useFormValidation";
import { validationRules as rule } from "../../utils/helpers/formValidation";
import { prepareImageFile } from "../../utils/helpers/imageUpload";

const initialFormState = {
  name: "",
  username: "",
  email: "",
  phone: "",
  current_password: "",
  password: "",
  password_confirmation: "",
  image: "",
  image_url: "",
  image_path: "",
  image_file: null,
};

const ProfileIcon = ({ name, className = "icon icon-1" }) => {
  const icons = {
    user: (
      <>
        <path d="M8 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0" />
        <path d="M6 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2" />
      </>
    ),
    mail: (
      <>
        <path d="M3 7a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v10a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2z" />
        <path d="M3 7l9 6l9 -6" />
      </>
    ),
    phone: (
      <>
        <path d="M5 4h4l2 5l-2.5 1.5a11 11 0 0 0 5 5l1.5 -2.5l5 2v4a2 2 0 0 1 -2 2a16 16 0 0 1 -15 -15a2 2 0 0 1 2 -2" />
      </>
    ),
    key: (
      <>
        <path d="M16.555 3.843l3.602 3.602a2.877 2.877 0 0 1 0 4.069l-2.643 2.643a2.877 2.877 0 0 1 -4.069 0l-.301 -.301l-6.558 6.558a2 2 0 0 1 -1.239 .578l-.175 .008h-1.172a1 1 0 0 1 -.993 -.883l-.007 -.117v-1.172a2 2 0 0 1 .467 -1.284l.119 -.13l.414 -.414h2v-2h2v-2l2.144 -2.144l-.301 -.301a2.877 2.877 0 0 1 0 -4.069l2.643 -2.643a2.877 2.877 0 0 1 4.069 0z" />
        <path d="M15 9h.01" />
      </>
    ),
    upload: (
      <>
        <path d="M12 3v12" />
        <path d="M16 7l-4 -4l-4 4" />
        <path d="M20 21h-16" />
      </>
    ),
  };

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}>
      <path stroke="none" d="M0 0h24v24H0z" fill="none" />
      {icons[name] || icons.user}
    </svg>
  );
};

const InputWithIcon = ({ icon, label, helper, error, required = false, formId = "profile", ...props }) => (
  <div className="mb-3">
    <label className="form-label">{label}<RequiredMark optional={!required} /></label>
    <div className="input-icon">
      <span className="input-icon-addon">
        <ProfileIcon name={icon} />
      </span>
      <input className={`form-control${error ? " is-invalid" : ""}`} aria-invalid={Boolean(error) || undefined} {...props} />
    </div>
    <FieldFeedback formId={formId} name={props.name} error={error} hint={helper} />
  </div>
);

const getProfileImage = (profile = {}) =>
  resolveAssetUrl(profile.image_url || profile.image_path || profile.image, "");

const ProfilePage = ({ profile, isLoading, error, fetchUserProfile, updateUserProfile }) => {
  const fileInputRef = useRef(null);
  const [formState, setFormState] = useState(initialFormState);
  const [imagePreview, setImagePreview] = useState("");
  const formRef = useRef(null);
  const validationSchema = useMemo(() => ({
    name: [rule.required("Nama lengkap wajib diisi."), rule.maxLength(255)],
    username: [rule.required("Username wajib diisi."), rule.maxLength(255)],
    email: [rule.required("Email wajib diisi."), rule.email(), rule.maxLength(255)],
    phone: rule.maxLength(50),
    current_password: rule.requiredWhen((values) => Boolean(values?.password), "Password saat ini wajib diisi untuk mengganti password."),
    password: rule.minLength(8, "Password baru minimal 8 karakter."),
    password_confirmation: rule.sameAs("password", "Konfirmasi password baru tidak sama."),
  }), []);
  const validation = useFormValidation({ values: formState, schema: validationSchema, formId: "profile", focusRoot: formRef });

  const roleLabel = useMemo(
    () => profile?.role || profile?.role_name || profile?.nama_role || profile?.roles?.[0]?.name || "-",
    [profile],
  );

  useEffect(() => {
    if (!profile) {
      fetchUserProfile();
    }
  }, [fetchUserProfile, profile]);

  useEffect(() => {
    if (!profile) {
      return;
    }

    setFormState((current) => ({
      ...current,
      name: profile.name || "",
      username: profile.username || "",
      email: profile.email || "",
      phone: profile.phone || "",
      image: profile.image || "",
      image_url: profile.image_url || "",
      image_path: profile.image_path || "",
      image_file: null,
    }));
    setImagePreview(getProfileImage(profile));
  }, [profile]);

  useEffect(() => () => {
    if (imagePreview.startsWith("blob:")) URL.revokeObjectURL(imagePreview);
  }, [imagePreview]);

  const updateField = (field, value) => {
    setFormState((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleImageChange = async (event) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    try {
      const compressedFile = await prepareImageFile(file, { preset: "avatar", label: "Foto profil" });
      updateField("image_file", compressedFile);
      setImagePreview(URL.createObjectURL(compressedFile));
    } catch (imageError) {
      event.target.value = "";
      toast.error(imageError?.message || "Foto profil tidak dapat diproses.");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validation.validateAll()) return;

    const success = await updateUserProfile(formState);

    if (success) {
      setFormState((current) => ({
        ...current,
        current_password: "",
        password: "",
        password_confirmation: "",
        image_file: null,
      }));
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  return (
    <div className="page-wrapper">
      <div className="page-header d-print-none">
        <div className="container-xl">
          <div className="row g-2 align-items-center">
            <div className="col">
              <div className="page-pretitle text-primary fw-bold">Akun</div>
              <h2 className="page-title">Profil Akun</h2>
              <div className="text-secondary mt-1">Kelola identitas dan keamanan akun yang sedang login.</div>
            </div>
          </div>
        </div>
      </div>

      <div className="page-body">
        <div className="container-xl">
          {error ? (
            <div className="alert alert-danger" role="alert">
              {error?.message || error}
            </div>
          ) : null}

          <form ref={formRef} className="row row-cards" onSubmit={handleSubmit} noValidate>
            <div className="col-12">
              <FormValidationNotice errors={validation.errors} />
            </div>
            <div className="col-lg-4">
              <div className="card h-100">
                <div className="card-body text-center">
                  <div className="mb-3 d-flex justify-content-center">
                    {imagePreview ? (
                      <span
                        className="avatar avatar-xl rounded-circle"
                        style={{
                          width: 112,
                          height: 112,
                          backgroundImage: `url(${imagePreview})`,
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                        }}></span>
                    ) : (
                      <UserAvatar
                        fullName={formState.name || formState.username || formState.email}
                        imageUrl=""
                        size="xl"
                        className="rounded-circle"
                      />
                    )}
                  </div>

                  <h3 className="mb-1">{formState.name || "User"}</h3>
                  <div className="text-secondary">{formState.email || "-"}</div>
                  <div className="mt-3">
                    <span className="badge bg-primary-lt">{roleLabel}</span>
                  </div>

                  <div className="mt-4">
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="d-none"
                      accept="image/png,image/jpeg,image/jpg,image/webp"
                      onChange={handleImageChange}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-primary w-100"
                      onClick={() => fileInputRef.current?.click()}>
                      <ProfileIcon name="upload" />
                      Ganti Foto Profil
                    </button>
                    <div className="form-hint mt-2">JPG, PNG, atau WEBP maksimal 5 MB. Dioptimalkan otomatis.</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-8">
              <div className="card">
                <div className="card-header">
                  <div>
                    <h3 className="card-title mb-0">Informasi Akun</h3>
                    <p className="text-secondary m-0">Ubah data dasar pemilik akun.</p>
                  </div>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6">
                      <InputWithIcon
                        {...validation.fieldProps("name")}
                        icon="user"
                        label="Nama Lengkap"
                        required
                        error={validation.errors.name}
                        type="text"
                        placeholder="nama lengkap"
                        value={formState.name}
                        onChange={(event) => updateField("name", event.target.value)}
                      />
                    </div>
                    <div className="col-md-6">
                      <InputWithIcon
                        {...validation.fieldProps("username")}
                        icon="user"
                        label="Username"
                        required
                        error={validation.errors.username}
                        helper="Username harus unik dan dapat digunakan untuk login."
                        type="text"
                        placeholder="username"
                        value={formState.username}
                        onChange={(event) => updateField("username", event.target.value)}
                      />
                    </div>
                    <div className="col-md-6">
                      <InputWithIcon
                        {...validation.fieldProps("email")}
                        icon="mail"
                        label="Email"
                        required
                        error={validation.errors.email}
                        helper="Gunakan email aktif dengan format yang valid."
                        type="email"
                        placeholder="email"
                        value={formState.email}
                        onChange={(event) => updateField("email", event.target.value)}
                      />
                    </div>
                    <div className="col-md-6">
                      <InputWithIcon
                        {...validation.fieldProps("phone")}
                        icon="phone"
                        label="Nomor HP"
                        error={validation.errors.phone}
                        helper="Maksimal 50 karakter."
                        type="text"
                        placeholder="nomor hp"
                        value={formState.phone}
                        onChange={(event) => {
                          const value = event.target.value.slice(0, 14);
                          if (/^\d*$/.test(value)) {
                            updateField("phone", value);
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="card mt-3">
                <div className="card-header">
                  <div>
                    <h3 className="card-title mb-0">Keamanan Akun</h3>
                    <p className="text-secondary m-0">Kosongkan password jika tidak ingin menggantinya.</p>
                  </div>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-12">
                      <InputWithIcon
                        {...validation.fieldProps("current_password")}
                        icon="key"
                        label="Password Saat Ini"
                        error={validation.errors.current_password}
                        required={Boolean(formState.password)}
                        type="password"
                        placeholder="password saat ini"
                        value={formState.current_password}
                        onChange={(event) => updateField("current_password", event.target.value)}
                        helper="Isi jika ingin mengganti password."
                      />
                    </div>
                    <div className="col-md-6">
                      <InputWithIcon
                        {...validation.fieldProps("password")}
                        icon="key"
                        label="Password Baru"
                        error={validation.errors.password}
                        helper="Minimal 8 karakter; kosongkan bila tidak ingin mengganti password."
                        type="password"
                        placeholder="minimal 8 karakter"
                        value={formState.password}
                        onChange={(event) => updateField("password", event.target.value)}
                      />
                    </div>
                    <div className="col-md-6">
                      <InputWithIcon
                        {...validation.fieldProps("password_confirmation")}
                        icon="key"
                        label="Konfirmasi Password"
                        error={validation.errors.password_confirmation}
                        required={Boolean(formState.password)}
                        helper="Harus sama persis dengan password baru."
                        type="password"
                        placeholder="ulangi password baru"
                        value={formState.password_confirmation}
                        onChange={(event) => updateField("password_confirmation", event.target.value)}
                      />
                    </div>
                  </div>
                </div>
                <div className="card-footer d-flex justify-content-end">
                  <button type="submit" className="btn btn-primary" disabled={isLoading}>
                    {isLoading ? (
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    ) : (
                      <ProfileIcon name="user" />
                    )}
                    Simpan Perubahan
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  profile: state?.auth?.login?.profile,
  isLoading: state?.auth?.login?.loading,
  error: state?.auth?.login?.error,
});

export default connect(mapStateToProps, {
  fetchUserProfile,
  updateUserProfile,
})(ProfilePage);
